import { useReportOnce } from "@/hooks/useReportOnce";
import { analytics } from "@/lib/analytics";
import { normalizeQuotedCartLines, summarize } from "@/lib/reorder-diff";
import type { ReorderLine } from "@/lib/reorder-diff";
import { formatPrice } from "@/utils/money";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { cn } from "@repo/design-system/utils";
import { AlertTriangle } from "lucide-react";
import { memo, useMemo } from "react";

type StoreSellerCart = SalonStockerHttpTypes.StoreSellerCart;

export interface QuoteDiffBannerProps {
  /** The carts being shown. Only lines carrying a quote are considered. */
  carts: StoreSellerCart[];
  className?: string;
}

/** "2 items changed price" / "1 item is no longer available", joined naturally. */
function buildSummaryText(repriced: number, unavailable: number): string {
  const parts: string[] = [];
  if (repriced > 0) {
    parts.push(
      `${repriced} ${repriced === 1 ? "item has" : "items have"} changed price`
    );
  }
  if (unavailable > 0) {
    parts.push(
      `${unavailable} ${unavailable === 1 ? "item is" : "items are"} no longer available`
    );
  }
  return parts.join(" and ");
}

function ChangedLine({ line }: { line: ReorderLine }) {
  const isUnavailable = line.status === "unavailable";

  return (
    <li className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      <span className="font-medium text-gray-900">{line.title}</span>
      {isUnavailable ? (
        <span className="text-gray-600">no longer available</span>
      ) : (
        <span className="text-gray-600">
          <span className="line-through">
            {formatPrice(line.old_price ?? 0)}
          </span>
          {" → "}
          <span className="font-medium text-gray-900">
            {formatPrice(line.new_price ?? 0)}
          </span>
        </span>
      )}
    </li>
  );
}

/**
 * "What moved since the quote" banner (KAN-39).
 *
 * An operator builds a salon's cart and sends a link; the salon may open it a
 * day later, by which point the 6h silent reprice has almost certainly run. This
 * says what changed before they act on it, rather than letting them notice a
 * different total at checkout.
 *
 * Classification comes from the server and is read through the `reorder-diff`
 * module - the single owner of changed-line logic (ADR-0002) - so the tally and
 * vocabulary match every other reorder surface rather than being re-derived here.
 *
 * Renders nothing when nothing moved: a banner that says "all good" on every
 * visit trains people to ignore the one that matters.
 */
export const QuoteDiffBanner = memo(function QuoteDiffBanner({
  carts,
  className
}: QuoteDiffBannerProps) {
  const lines = useMemo(
    () => normalizeQuotedCartLines(carts.flatMap((cart) => cart.items)),
    [carts]
  );

  const counts = useMemo(() => summarize(lines), [lines]);
  const changedLines = useMemo(
    () => lines.filter((line) => line.status !== "available"),
    [lines]
  );

  const unchanged = counts.added - counts.repriced;
  const hasChanges = counts.repriced > 0 || counts.unavailable > 0;

  // One event per landing, not per render. Keyed on the tally, so a cart that
  // genuinely changes underneath the salon (they remove an unavailable line)
  // reports its new shape rather than staying silent.
  useReportOnce(
    hasChanges ? `${unchanged}:${counts.repriced}:${counts.unavailable}` : null,
    () => {
      analytics.quoteDiffViewed({
        unchanged_count: unchanged,
        repriced_count: counts.repriced,
        unavailable_count: counts.unavailable
      });
    }
  );

  if (!hasChanges) {
    return null;
  }

  return (
    <div
      role="status"
      className={cn(
        "rounded-lg border border-amber-200 bg-amber-50 p-4",
        className
      )}
    >
      <div className="flex gap-3">
        <AlertTriangle
          className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600"
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900">
            Some items changed since your quote
          </p>
          <p className="mt-0.5 text-sm text-gray-700">
            {buildSummaryText(counts.repriced, counts.unavailable)}.
          </p>
          <ul className="mt-2 space-y-1 text-sm">
            {changedLines.map((line, index) => (
              <ChangedLine
                key={line.variant_id ?? `${line.title}-${index}`}
                line={line}
              />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
});
